import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { goalsAPI } from '../../api';
import { Spinner } from '../../components/ui';
import {
  Target, Plus, Trash2, TrendingUp, TrendingDown, Sparkles,
  Calendar, Flag, X, Check, AlertCircle, RefreshCw, ChevronDown,
  ChevronUp, Trophy
} from 'lucide-react';

/**
 * GoalsPage — /progress/goals
 *
 * User-defined body composition / training targets. Each goal has:
 *   - metric (weight, body fat, chest, sessions/week, etc.)
 *   - direction (down for weight loss, up for muscle gain, target for sessions)
 *   - starting value, target value, optional deadline
 *
 * The page renders active and completed goals as cards with:
 *   - Current vs target, progress bar
 *   - Days remaining (or "X days ago" if past)
 *   - Delete / Mark complete actions
 *
 * A "+ New goal" button opens a modal with metric + direction + value inputs.
 */
export const GoalsPage = () => {
  const { t } = useTranslation();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('active');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await goalsAPI.list();
      setGoals(res.data);
    } catch (err) {
      console.error('Failed to load goals:', err);
      setError(t('goalsPage.error', { defaultValue: 'Could not load goals.' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === 'active') return goals.filter((g) => !g.is_completed);
    if (filter === 'completed') return goals.filter((g) => g.is_completed);
    return goals;
  }, [goals, filter]);

  return (
    <div className="space-y-6" data-testid="goals-page">
      <PageHeader
        onAdd={() => setShowForm(true)}
        filter={filter}
        setFilter={setFilter}
        onRefresh={load}
        t={t}
      />

      {error && <ErrorState message={error} onRetry={load} t={t} />}

      {loading && !goals.length ? (
        <div className="flex items-center justify-center min-h-[420px]">
          <Spinner size="lg" />
        </div>
      ) : !goals.length ? (
        <EmptyState onAdd={() => setShowForm(true)} t={t} />
      ) : (
        <>
          {filter === 'active' && filtered.length === 0 && (
            <AllDoneState t={t} />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="goals-list">
            {filtered.map((g) => (
              <GoalCard
                key={g.id}
                goal={g}
                onUpdate={load}
                t={t}
              />
            ))}
          </div>
        </>
      )}

      {showForm && (
        <GoalForm
          onClose={() => setShowForm(false)}
          onSave={async () => { setShowForm(false); await load(); }}
          t={t}
        />
      )}
    </div>
  );
};

// ===========================================================================
// Sub-components
// ===========================================================================

function PageHeader({ onAdd, filter, setFilter, onRefresh, t }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <span className="hero-kicker">
          {t('goalsPage.kicker', { defaultValue: 'Targets' })}
        </span>
        <h1 className="mt-2 font-display text-3xl uppercase text-[var(--color-text)]">
          {t('goalsPage.title', { defaultValue: 'Your goals' })}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-2xl">
          {t('goalsPage.subtitle', {
            defaultValue: 'Set body composition and training targets. Watch yourself close the gap.',
          })}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <FilterPills value={filter} onChange={setFilter} t={t} />
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm font-medium hover:bg-white/[0.08] transition-colors"
          data-testid="goals-refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold hover:from-primary-300 hover:to-primary-200 transition-all"
          data-testid="goals-add-btn"
        >
          <Plus className="w-4 h-4" />
          {t('goalsPage.newGoal', { defaultValue: 'New goal' })}
        </button>
      </div>
    </div>
  );
}

function FilterPills({ value, onChange, t }) {
  const options = [
    { v: 'active', label: t('goalsPage.filterActive', { defaultValue: 'Active' }) },
    { v: 'completed', label: t('goalsPage.filterCompleted', { defaultValue: 'Completed' }) },
    { v: 'all', label: t('goalsPage.filterAll', { defaultValue: 'All' }) },
  ];
  return (
    <div
      className="inline-flex p-1 rounded-2xl border border-[var(--color-border)] bg-white/[0.04]"
      role="tablist"
    >
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
              active
                ? 'bg-primary-300 text-[#0a1208]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
            data-testid={`goals-filter-${o.v}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function GoalCard({ goal, onUpdate, t }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [marking, setMarking] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(t('goalsPage.confirmDelete', { defaultValue: 'Delete this goal?' }))) return;
    setDeleting(true);
    try {
      await goalsAPI.delete(goal.id);
      await onUpdate();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkComplete = async () => {
    setMarking(true);
    try {
      await goalsAPI.update(goal.id, { completed_at: new Date().toISOString() });
      await onUpdate();
    } catch (e) {
      console.error(e);
    } finally {
      setMarking(false);
    }
  };

  const colorClasses = goal.is_completed
    ? 'border-[rgba(213,255,123,0.4)] bg-gradient-to-br from-[rgba(213,255,123,0.08)] to-transparent'
    : 'border-[var(--color-border)] bg-[rgba(8,13,11,0.5)]';

  const directionIcon = goal.direction === 'down' ? TrendingDown : goal.direction === 'up' ? TrendingUp : Target;
  const DirIcon = directionIcon;

  return (
    <div
      className={`rounded-2xl border p-5 ${colorClasses}`}
      data-testid={`goal-card-${goal.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`shrink-0 p-2 rounded-xl border ${goal.is_completed ? 'border-primary-200/40 bg-primary-200/10 text-primary-100' : 'border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text-muted)]'}`}>
            {goal.is_completed ? <Trophy className="w-4 h-4" /> : <DirIcon className="w-4 h-4" />}
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg text-[var(--color-text)] truncate">
              {goal.metric_label}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              {goal.direction === 'down' ? t('goalsPage.dirDown', { defaultValue: 'Decrease' }) :
               goal.direction === 'up' ? t('goalsPage.dirUp', { defaultValue: 'Increase' }) :
               t('goalsPage.dirTarget', { defaultValue: 'Target' })}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          data-testid={`goal-expand-${goal.id}`}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Current vs Target */}
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {t('goalsPage.current', { defaultValue: 'Current' })}
          </div>
          <div className="font-display text-2xl text-[var(--color-text)] leading-none">
            {goal.current_value != null ? goal.current_value.toFixed(1) : '—'}
            <span className="text-xs text-[var(--color-text-muted)] ml-1">{goal.unit}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {t('goalsPage.target', { defaultValue: 'Target' })}
          </div>
          <div className="font-display text-2xl text-[var(--color-text-muted)] leading-none">
            {goal.target_value}
            <span className="text-xs text-[var(--color-text-muted)] ml-1">{goal.unit}</span>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
          <span>{t('goalsPage.progress', { defaultValue: 'Progress' })}</span>
          <span className={goal.is_completed ? 'text-primary-100' : 'text-[var(--color-text)]'}>
            {goal.progress_pct.toFixed(0)}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.04] border border-[var(--color-border)] overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              goal.is_completed
                ? 'bg-gradient-to-r from-primary-500 to-primary-300'
                : 'bg-gradient-to-r from-primary-500/70 to-primary-300/70'
            }`}
            style={{ width: `${Math.min(goal.progress_pct, 100)}%` }}
          />
        </div>
      </div>

      {/* Deadline */}
      {goal.deadline && (
        <div className="mt-3 flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
          <Calendar className="w-3.5 h-3.5" />
          {goal.days_remaining != null && goal.days_remaining >= 0 ? (
            <span>
              {t('goalsPage.daysLeft', {
                defaultValue: `${goal.days_remaining} day${goal.days_remaining === 1 ? '' : 's'} left`,
              })}
            </span>
          ) : goal.days_remaining != null ? (
            <span className="text-amber-200">
              {t('goalsPage.daysOver', {
                defaultValue: `${Math.abs(goal.days_remaining)} day${Math.abs(goal.days_remaining) === 1 ? '' : 's'} overdue`,
              })}
            </span>
          ) : null}
        </div>
      )}

      {/* Achieved banner */}
      {goal.is_completed && (
        <div className="mt-3 rounded-xl border border-primary-200/30 bg-primary-200/10 px-3 py-2 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary-100" />
          <span className="text-xs text-primary-100 font-medium">
            {t('goalsPage.achieved', { defaultValue: 'Goal achieved' })}
          </span>
        </div>
      )}

      {/* Expanded actions */}
      {expanded && (
        <div className="mt-4 pt-3 border-t border-[var(--color-border)] flex items-center gap-2">
          {!goal.is_completed && (
            <button
              type="button"
              onClick={handleMarkComplete}
              disabled={marking}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-primary-100 hover:bg-primary-200/10 transition-colors disabled:opacity-50"
              data-testid={`goal-mark-${goal.id}`}
            >
              <Check className="w-3.5 h-3.5" />
              {t('goalsPage.markComplete', { defaultValue: 'Mark complete' })}
            </button>
          )}
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs text-[var(--color-text-muted)] hover:text-red-200 hover:bg-red-500/10 transition-colors disabled:opacity-50"
            data-testid={`goal-delete-${goal.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('goalsPage.delete', { defaultValue: 'Delete' })}
          </button>
        </div>
      )}
    </div>
  );
}

function EmptyState({ onAdd, t }) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[rgba(8,13,11,0.4)] p-12 text-center">
      <Target className="w-8 h-8 text-[var(--color-text-muted)] mx-auto mb-3" />
      <p className="font-display text-lg text-[var(--color-text)]">
        {t('goalsPage.empty.title', { defaultValue: 'No goals yet' })}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
        {t('goalsPage.empty.hint', {
          defaultValue: 'Set your first target — weight, body fat, weekly sessions — and we\'ll chart your progress.',
        })}
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold hover:from-primary-300 hover:to-primary-200 transition-all"
        data-testid="goals-empty-add"
      >
        <Plus className="w-4 h-4" />
        {t('goalsPage.empty.cta', { defaultValue: 'Set your first goal' })}
      </button>
    </div>
  );
}

function AllDoneState({ t }) {
  return (
    <div className="rounded-2xl border border-primary-200/30 bg-gradient-to-br from-[rgba(213,255,123,0.08)] to-transparent p-8 text-center">
      <Trophy className="w-10 h-10 text-primary-100 mx-auto mb-2" />
      <p className="font-display text-xl text-[var(--color-text)]">
        {t('goalsPage.allDone', { defaultValue: 'All targets met' })}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
        {t('goalsPage.allDoneHint', {
          defaultValue: 'You\'re crushing it. Set a new challenge to keep the momentum going.',
        })}
      </p>
    </div>
  );
}

function ErrorState({ message, onRetry, t }) {
  return (
    <div className="rounded-2xl border border-red-500/30 bg-red-500/10 text-red-200 p-4 flex items-center gap-3">
      <AlertCircle className="w-5 h-5 shrink-0" />
      <p className="text-sm flex-1">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-xs font-semibold hover:bg-white/[0.08] transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {t('goalsPage.retry', { defaultValue: 'Retry' })}
      </button>
    </div>
  );
}

// ===========================================================================
// New-goal form
// ===========================================================================

const METRIC_OPTIONS = [
  { value: 'weight', label: 'Weight', unit: 'kg', direction: 'down' },
  { value: 'body_fat', label: 'Body Fat', unit: '%', direction: 'down' },
  { value: 'muscle mass', label: 'Muscle Mass', unit: 'kg', direction: 'up' },
  { value: 'chest', label: 'Chest', unit: 'cm', direction: 'up' },
  { value: 'waist', label: 'Waist', unit: 'cm', direction: 'down' },
  { value: 'hips', label: 'Hips', unit: 'cm', direction: 'down' },
  { value: 'biceps_left', label: 'Biceps (L)', unit: 'cm', direction: 'up' },
  { value: 'biceps_right', label: 'Biceps (R)', unit: 'cm', direction: 'up' },
  { value: 'thigh_left', label: 'Thigh (L)', unit: 'cm', direction: 'up' },
  { value: 'sessions_per_week', label: 'Sessions / week', unit: 'sessions', direction: 'target' },
];

function GoalForm({ onClose, onSave, t }) {
  const [metric, setMetric] = useState('weight');
  const [targetValue, setTargetValue] = useState('');
  const [startingValue, setStartingValue] = useState('');
  const [unit, setUnit] = useState('kg');
  const [deadline, setDeadline] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedMetric = METRIC_OPTIONS.find((m) => m.value === metric);

  // When metric changes, default the unit and direction
  const handleMetricChange = (newMetric) => {
    setMetric(newMetric);
    const opt = METRIC_OPTIONS.find((m) => m.value === newMetric);
    if (opt) {
      setUnit(opt.unit);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetValue) {
      setError(t('goalsPage.form.errorTarget', { defaultValue: 'Set a target value' }));
      return;
    }
    if (!startingValue && metric !== 'sessions_per_week') {
      setError(t('goalsPage.form.errorStart', { defaultValue: 'Set a starting value (or close + log one first)' }));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const direction = selectedMetric?.direction || 'target';
      const starting = startingValue ? parseFloat(startingValue) : parseFloat(targetValue);
      await goalsAPI.create({
        metric,
        direction,
        target_value: parseFloat(targetValue),
        starting_value: starting,
        unit,
        deadline: deadline ? new Date(deadline).toISOString() : null,
        notes: notes || null,
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || t('goalsPage.form.errorSave', { defaultValue: 'Failed to save goal' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="goal-form-modal">
      <div className="rounded-2xl border border-[var(--color-border)] bg-[#0d1512] w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
          <div>
            <span className="hero-kicker">{t('goalsPage.form.kicker', { defaultValue: 'New' })}</span>
            <h2 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
              {t('goalsPage.form.title', { defaultValue: 'Set a goal' })}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            data-testid="goal-form-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
              {t('goalsPage.form.metric', { defaultValue: 'Metric' })}
            </label>
            <select
              value={metric}
              onChange={(e) => handleMetricChange(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
              data-testid="goal-form-metric"
            >
              {METRIC_OPTIONS.map((m) => (
                <option key={m.value} value={m.value} className="bg-[#0a1208]">
                  {m.label} ({m.direction === 'down' ? '↓' : m.direction === 'up' ? '↑' : '→'} {m.unit})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
                {t('goalsPage.form.startingValue', { defaultValue: 'Starting' })}
              </label>
              <input
                type="number"
                step="0.1"
                value={startingValue}
                onChange={(e) => setStartingValue(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
                data-testid="goal-form-start"
                placeholder="e.g. 90"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
                {t('goalsPage.form.targetValue', { defaultValue: 'Target' })}
              </label>
              <input
                type="number"
                step="0.1"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
                data-testid="goal-form-target"
                placeholder="e.g. 85"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
                {t('goalsPage.form.unit', { defaultValue: 'Unit' })}
              </label>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
              >
                <option value="kg" className="bg-[#0a1208]">kg</option>
                <option value="lbs" className="bg-[#0a1208]">lbs</option>
                <option value="%" className="bg-[#0a1208]">%</option>
                <option value="cm" className="bg-[#0a1208]">cm</option>
                <option value="inches" className="bg-[#0a1208]">inches</option>
                <option value="sessions" className="bg-[#0a1208]">sessions</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
                {t('goalsPage.form.deadline', { defaultValue: 'Deadline (opt.)' })}
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40"
                data-testid="goal-form-deadline"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-1.5">
              {t('goalsPage.form.notes', { defaultValue: 'Notes' })}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t('goalsPage.form.notesPlaceholder', { defaultValue: 'Why this goal? What\'s the plan?' })}
              className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm focus:outline-none focus:border-primary-200/40 resize-none"
            />
          </div>

          {error && (
            <div className="px-3 py-2 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04]"
            >
              {t('goalsPage.form.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold hover:from-primary-300 hover:to-primary-200 transition-all disabled:opacity-50"
              data-testid="goal-form-save"
            >
              {saving ? t('goalsPage.form.saving', { defaultValue: 'Saving…' }) : t('goalsPage.form.save', { defaultValue: 'Set goal' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default GoalsPage;
